/** `app` log label for this deployable (02-architecture §2, 07-logging §labels). */
export const APP_NAME = 'web-admin'

export interface AppEnv {
  apiBaseUrl: string
  environment: string
  /** Grafana Faro collector URL; empty means telemetry is off. */
  faroUrl: string | null
  /** Браузерный ключ Яндекс Карт (JS API 2.1); пусто — карта скажет, что её нет. */
  yandexMapsKey: string
  /**
   * Показывать пометки «нет в API». Пусто — включены в разработке и
   * выключены в сборке; `'true'`/`'false'` задают это явно.
   */
  showApiGaps: boolean
  /** Новые функции за флагами `VITE_FEATURE_<ИМЯ>` (docs/conventions.md). */
  features: {
    /** Сторис на главной, ведутся из админки (`VITE_FEATURE_STORIES`). */
    stories: boolean
    /** Новости: блок на главной, список и экран новости (`VITE_FEATURE_NEWS`). */
    news: boolean
    /** Отзывы об аптеке после заказа (`VITE_FEATURE_REVIEWS`). */
    reviews: boolean
    /**
     * Пул ключей Яндекс Карт: карта берёт ключ только с сервера (пул пуст —
     * ошибка); раздел ключей в интеграции «Карты» (`VITE_FEATURE_MAPS_KEYS`).
     */
    mapsKeys: boolean
    /**
     * Уведомления сотрудника: раздел «Уведомления» со счётчиком, живые события
     * заказов и push в браузере (`VITE_FEATURE_NOTIFICATIONS`).
     */
    notifications: boolean
  }
  /**
   * Веб-приложение Firebase для push в браузере. null — чего-то из ключей нет:
   * push недоступен, список уведомлений и живые события работают.
   */
  firebase: FirebaseEnv | null
  /**
   * Сторис из mock-хранилища в браузере вместо API — только для показа без
   * бэкенда. Ручки есть (`/stories`, `/admin/stories`): по умолчанию API,
   * mock — лишь при `'true'`.
   */
  storiesMock: boolean
  /** Новости из mock-хранилища в браузере — только при `'true'`; по умолчанию API. */
  newsMock: boolean
  /** Отзывы из mock-хранилища в браузере — только при `'true'`; по умолчанию API. */
  reviewsMock: boolean
  /** Адреса API этой среды, которые техник FOM вписывает в программы аптеки. */
  fom: FomEnv
}

/**
 * Полные адреса сервера для кассы и SITE.exe. `apiBaseUrl` для этого не
 * годится: он относительный (`/api/v1`) и ведёт к прокси админки, а программы
 * в аптеке ходят прямо на API. Пусто — адрес в среде не задан.
 */
export interface FomEnv {
  /** Адрес сервиса для кассы (CASHBACK.exe) по https, без пути. */
  origin: string
  /** Тот же сервис без TLS — для касс, которые не умеют https. */
  plainOrigin: string
}

/** Публичная конфигурация веб-приложения Firebase и VAPID-ключ для `getToken`. */
export interface FirebaseEnv {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  vapidKey: string
}

/** The subset of import.meta.env this app reads; tests pass plain objects. */
export interface EnvSource {
  readonly VITE_API_BASE_URL?: string
  readonly VITE_ENV?: string
  readonly VITE_FARO_URL?: string
  readonly VITE_YANDEX_MAPS_KEY?: string
  readonly VITE_SHOW_API_GAPS?: string
  readonly VITE_FEATURE_STORIES?: string
  readonly VITE_STORIES_MOCK?: string
  readonly VITE_FEATURE_NEWS?: string
  readonly VITE_NEWS_MOCK?: string
  readonly VITE_FEATURE_REVIEWS?: string
  readonly VITE_REVIEWS_MOCK?: string
  readonly VITE_FOM_ORIGIN?: string
  readonly VITE_FOM_PLAIN_ORIGIN?: string
  readonly VITE_FEATURE_MAPS_KEYS?: string
  readonly VITE_FEATURE_NOTIFICATIONS?: string
  readonly VITE_FIREBASE_API_KEY?: string
  readonly VITE_FIREBASE_AUTH_DOMAIN?: string
  readonly VITE_FIREBASE_PROJECT_ID?: string
  readonly VITE_FIREBASE_STORAGE_BUCKET?: string
  readonly VITE_FIREBASE_MESSAGING_SENDER_ID?: string
  readonly VITE_FIREBASE_APP_ID?: string
  readonly VITE_FIREBASE_VAPID_KEY?: string
  readonly DEV?: boolean
}

/**
 * Флаг новой функции `VITE_FEATURE_<ИМЯ>`: включена только значением `'true'`.
 * Пусто, опечатка или переменной нет — выключена, так что функция не попадёт
 * к пользователям, пока её явно не включат в `.env` своего стенда.
 */
export function featureFlag(value: string | undefined): boolean {
  return value === 'true'
}

/**
 * Firebase из env. Без ключа API, приложения или VAPID токен браузера не
 * получить — тогда null, и экран скажет, что push в этой сборке не настроен.
 */
export function readFirebase(source: EnvSource): FirebaseEnv | null {
  const apiKey = source.VITE_FIREBASE_API_KEY ?? ''
  const appId = source.VITE_FIREBASE_APP_ID ?? ''
  const vapidKey = source.VITE_FIREBASE_VAPID_KEY ?? ''
  if (!apiKey || !appId || !vapidKey) return null
  return {
    apiKey,
    authDomain: source.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    projectId: source.VITE_FIREBASE_PROJECT_ID ?? '',
    storageBucket: source.VITE_FIREBASE_STORAGE_BUCKET ?? '',
    messagingSenderId: source.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
    appId,
    vapidKey,
  }
}

export function readEnv(source: EnvSource): AppEnv {
  return {
    apiBaseUrl: source.VITE_API_BASE_URL ?? '/api/v1',
    environment: source.VITE_ENV ?? 'local',
    faroUrl: source.VITE_FARO_URL ? source.VITE_FARO_URL : null,
    yandexMapsKey: source.VITE_YANDEX_MAPS_KEY ?? '',
    showApiGaps: source.VITE_SHOW_API_GAPS
      ? source.VITE_SHOW_API_GAPS === 'true'
      : (source.DEV ?? false),
    features: {
      stories: featureFlag(source.VITE_FEATURE_STORIES),
      news: featureFlag(source.VITE_FEATURE_NEWS),
      reviews: featureFlag(source.VITE_FEATURE_REVIEWS),
      mapsKeys: featureFlag(source.VITE_FEATURE_MAPS_KEYS),
      notifications: featureFlag(source.VITE_FEATURE_NOTIFICATIONS),
    },
    firebase: readFirebase(source),
    storiesMock: source.VITE_STORIES_MOCK === 'true',
    newsMock: source.VITE_NEWS_MOCK === 'true',
    reviewsMock: source.VITE_REVIEWS_MOCK === 'true',
    fom: {
      origin: trimSlash(source.VITE_FOM_ORIGIN),
      plainOrigin: trimSlash(source.VITE_FOM_PLAIN_ORIGIN),
    },
  }
}

/** Адрес без косой черты в конце: к нему дописывают путь. */
function trimSlash(value: string | undefined): string {
  return (value ?? '').trim().replace(/\/+$/, '')
}

export const env: AppEnv = readEnv(import.meta.env)
